import CompanyField from 'components/company-field/company-field';
import env from 'env';
import { useFormikContext } from 'formik';
import { useCompanyRouteQuery } from 'generated/graphql';
import ProDocuments from 'pages/pros/pro-form/pro-documents/pro-documents';
import React, { Fragment, FunctionComponent, useMemo } from 'react';
import { filter, pipe, uniq } from 'remeda';
import { Divider, Grid, Icon, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import { getToken } from 'utils';
import ProContract from '../pro-contract/pro-contract';
import { ProCreditNotes } from '../pro-credit-notes';
import { ProFormUpdateFields } from '../pro-form-update-fields-type';
import { ProInvoices } from '../pro-invoices';
import ProMandates from '../pro-mandates/pro-mandates';
import ProSubscriptions from '../pro-subscriptions/pro-subscriptions';

const DocumentGrid = styled(Grid)`
  div {
    padding: 16px 6px !important;

    &:first-child {
      padding-left: 12px !important;
    }

    &:last-child {
      padding-right: 12px !important;
    }
  }
`;

const DocumentCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  color: #333;
  text-decoration: none;
  transition: all 0.2s ease;
  height: 100%;

  span {
    display: flex;
    align-items: center;
    text-align: center;
    min-height: 30px;
    line-height: 15px;
  }

  i {
    font-size: 2em;
    margin-top: 10px;
    margin-bottom: 4px;
  }

  &:hover {
    background-color: #f9f9f9;
    text-decoration: none;
  }
`;

type CompanyRouteProps = {
  proViewId: number;
  companyId?: number;
  hasCompanyChanged: boolean;
  bilikZoneId: number;
};

/**
 * CompanyRoute is rendered when a user is on the billing tab of the pro page.
 * It's responsible of handling all company stuff including billing, documents.
 */
const CompanyRoute: FunctionComponent<CompanyRouteProps> = ({
  proViewId,
  bilikZoneId,
  hasCompanyChanged,
}) => {
  const { values } = useFormikContext<ProFormUpdateFields>();

  const { data } = useCompanyRouteQuery({
    variables: {
      proPeopleIds: values.proPeople.map((proPerson) => proPerson.id),
      bilikZoneId: bilikZoneId,
    },
  });

  // Concat all proPerson emails and account emails from the current proView to make an billingEmail suggestion (uniq)
  const billingEmailSuggestions = useMemo(() => {
    const proPersonEmails =
      data?.proPerson.map((proPerson) => proPerson.email || 'null') || [];

    return pipe(
      proPersonEmails,
      filter((proPersonEmail) => proPersonEmail !== 'null'),
      uniq(),
    );
  }, [data?.proPerson]);

  const proPersonSuggestions = useMemo(
    () =>
      data?.proPerson
        .map((proPerson) => ({
          familyName: proPerson.familyName,
          givenName: proPerson.givenName,
          telephone: proPerson.telephone || 'null',
        }))
        .filter((proPerson) => proPerson.telephone !== 'null'),
    [data?.proPerson],
  );

  const [proContractKey, setProContractKey] = React.useState(0);

  return (
    <>
      <Divider horizontal>Ressources</Divider>
      <DocumentGrid columns={6}>
        <Grid.Column>
          <DocumentCard
            href="#"
            // Using onClick to be sure to give the latest token
            onClick={(e) => {
              e.preventDefault();
              window.open(
                `${env.API_URL}/form/pdf/base-contract/${data?.zohoOrganization?.[0].id}`,
              );
            }}
          >
            <Icon name="file pdf outline" />
            <span>Contrat vierge</span>
          </DocumentCard>
        </Grid.Column>
        <Grid.Column>
          <DocumentCard
            href="#"
            // Using onClick to be sure to give the latest token
            onClick={(e) => {
              e.preventDefault();
              window.open(
                `${
                  env.API_URL
                }/attachment/object-by-key-token?key=assets/documents/engagement_confidentialite_bilik${
                  data?.zohoOrganization?.[0].id === 2 ? '_aix' : ''
                }.pdf&token=${getToken()}`,
              );
            }}
          >
            <Icon name="file pdf outline" />
            <span>Engagement confidentialité Bilik</span>
          </DocumentCard>
        </Grid.Column>
        <Grid.Column>
          <DocumentCard
            href="#"
            // Using onClick to be sure to give the latest token
            onClick={(e) => {
              e.preventDefault();
              window.open(
                `${
                  env.API_URL
                }/attachment/object-by-key-token?key=assets/documents/checklist.pdf&token=${getToken()}`,
              );
            }}
          >
            <Icon name="file pdf outline" />
            <span>Checklist d&apos;entrée</span>
          </DocumentCard>
        </Grid.Column>
        <Grid.Column>
          <DocumentCard
            href={`https://docs.google.com/spreadsheets/d/19fBRD_jvU89HavIi9GveahVW_c6qSZa-y56nrqXinLs/edit#gid=0`}
            target="_blank"
            rel="noreferrer"
          >
            <Icon name="file excel outline" />
            <span>Grille des tarifs</span>
          </DocumentCard>
        </Grid.Column>
        <Grid.Column>
          <DocumentCard
            href="#"
            // Using onClick to be sure to give the latest token
            onClick={(e) => {
              e.preventDefault();
              window.open(
                `${
                  env.API_URL
                }/attachment/object-by-key-token?key=assets/documents/loxam_tarifs.pdf&token=${getToken()}`,
              );
            }}
          >
            <Icon name="file pdf outline" />
            <span>Accord Cadre Loxam</span>
          </DocumentCard>
        </Grid.Column>
        <Grid.Column>
          <DocumentCard
            href="#"
            // Using onClick to be sure to give the latest token
            onClick={(e) => {
              e.preventDefault();
              window.open(
                `${
                  env.API_URL
                }/attachment/object-by-key-token?key=assets/documents/ford_tableau_des_remises.pdf&token=${getToken()}`,
              );
            }}
          >
            <Icon name="file pdf outline" />
            <span>Accord Cadre Ford</span>
          </DocumentCard>
        </Grid.Column>
      </DocumentGrid>
      <Divider horizontal style={{ marginTop: '50px' }}>
        Entreprise
      </Divider>
      {/* An explanation on what to do when companyId doesn't exists */}
      {!values.proPresentation.companyId ? (
        <>
          <p>
            Vous devez créer une nouvelle entreprise pour cette fiche, ou en
            sélectionner une déjà existante. Une entreprise peut déjà exister
            dans le cas où deux fiches existent pour un même pro (fiches dans
            deux catégories différentes).
          </p>
          <Divider />
        </>
      ) : null}
      <CompanyField
        billingEmailSuggestions={billingEmailSuggestions}
        label="Sélectionner une entreprise de la liste"
        helpText="L'entreprise sélectionnée sera l'entreprise facturée pour les abonnements"
        name="proPresentation.companyId"
        value={values.proPresentation.companyId}
        onCompanyUpdated={() => setProContractKey((prev) => prev + 1)} // Force refresh of the proContract component
      />
      {/* Display organization actions only when a company is selected */}
      {values.proPresentation.companyId && !hasCompanyChanged ? (
        <Fragment>
          <Divider horizontal style={{ marginTop: '50px' }}>
            Mandats
          </Divider>
          <ProMandates
            companyId={values.proPresentation.companyId}
            receiptEmailSuggestions={billingEmailSuggestions}
            proPersonSuggestions={proPersonSuggestions}
          />
          <Divider horizontal style={{ marginTop: '50px' }}>
            Contrat
          </Divider>
          <ProContract
            key={`pro-contract-${values.proPresentation.companyId}-${proContractKey}`}
            companyId={values.proPresentation.companyId}
            proViewId={proViewId}
            receiptEmailSuggestions={billingEmailSuggestions}
            proPersonSuggestions={proPersonSuggestions}
          />

          <Divider horizontal style={{ marginTop: '50px' }}>
            Documents & Assurances
          </Divider>

          <ProDocuments companyId={values.proPresentation.companyId} />
          <Divider horizontal style={{ marginTop: '50px' }}>
            Abonnements
          </Divider>
          <ProSubscriptions companyId={values.proPresentation.companyId} />
          <Divider horizontal style={{ marginTop: '50px' }}>
            Factures
          </Divider>
          <ProInvoices companyId={values.proPresentation.companyId} />
          <Divider horizontal style={{ marginTop: '50px' }}>
            Avoirs
          </Divider>
          <ProCreditNotes companyId={values.proPresentation.companyId} />
        </Fragment>
      ) : null}

      {/* If the selected pro organization isn't yet commited to db */}
      {values.proPresentation.companyId && hasCompanyChanged ? (
        <Message color="orange">
          Veuillez{' '}
          <span>
            <Icon name="save" />
            Enregistrer
          </span>{' '}
          la fiche pour accéder aux informations liées à l&apos;entreprise.
        </Message>
      ) : null}
    </>
  );
};

export default CompanyRoute;
